import React, { useState } from "react";
import { CHART_1, CHART_2, CHART_3, INK, MUTED, GRID, PAPER, BAND, BAND_EDGE } from "./palette";

// ---- binomial: the exact binomial test's null distribution, with the counts its p adds up ----

// One bar per possible count k = 0…n, its height P(X = k) if the population's rate were π₀ (teal,
// the idea). The bars that go into the two-sided p, every count no more likely than the observed
// one on either side, are solid; the rest are pale. The observed count's bar is green (the data),
// a dashed line at its height shows the "no more likely" cut, and a bracket over each counted run
// gives that tail's sum. Props come from ChartData#binomial:
//   n, count (the observed x), pi0, expected (nπ₀), probs [P(X = k)], counted [bool],
//   lower / upper ({ from, to, sum } or null), p.
// Settings: countNoun ("hit", plural with an s; default "yes"), rateNoun ("hit rate").
// With altProbs (kind binomial_power) it draws the power picture instead: see PowerChart below.
// The tokens and the Legend/Tooltip conventions match ConceptChart.jsx.
const W = 640, H = 270;
const M = { top: 16, right: 16, bottom: 40, left: 44 };

const fmt = (x, places = 2) => (x < 0 ? "−" : "") + Math.abs(x).toFixed(places);
// APA style for values that can't exceed 1: ".0070".
const fmtR = (x, places = 4) => fmt(x, places).replace(/^(−?)0\./, "$1.");
const fmtProb = (x) => (x > 0 && x < 0.0001 ? "< .0001" : fmtR(x, 4));
// What follows "p": "= .021", or "< .001".
const pRel = (p) => (p < 0.001 ? "< .001" : `= ${fmtR(p, 3)}`);
const fmtCount = (v) => (Number.isInteger(v) ? String(v) : fmt(v, 1));
const upperFirst = (s) => s.charAt(0).toUpperCase() + s.slice(1);
const Halo = ({ children, ...props }) => <text fontSize="11" stroke={PAPER} strokeWidth="3" paintOrder="stroke" strokeLinejoin="round" {...props}>{children}</text>;

// kind: square (a bar), dash (a threshold line).
function Legend({ items }) {
	return (
		<div className="flex flex-wrap gap-x-5 gap-y-1 text-xs text-gray-600">
			{items.map(({ key, label, color, kind, opacity = 1 }) => (
				<span key={key || label} className="flex items-center gap-1.5">
					{kind === "dash"
						? <span className="inline-block w-4 border-t-2 border-dashed" style={{ borderColor: color }} />
						: <span className="inline-block h-2.5 w-2.5 rounded-sm" style={{ background: color, opacity }} />}
					{label}
				</span>
			))}
		</div>
	);
}

function Tooltip({ x, lines }) {
	const longest = Math.max(...lines.map((l) => String(l).length));
	const width = Math.max(170, Math.round(longest * 6.4) + 16), left = Math.min(Math.max(x + 8, M.left), W - M.right - width);
	return (
		<g pointerEvents="none" transform={`translate(${left},${M.top})`}>
			<rect width={width} height={10 + lines.length * 15} rx="3" fill={PAPER} stroke={GRID} />
			{lines.map((line, i) => (
				<text key={i} x="8" y={17 + i * 15} fontSize="11" fill={i === 0 ? MUTED : INK} fontWeight={i === 0 ? 400 : 600}>{line}</text>
			))}
		</g>
	);
}

// Probability ticks: three to five steps up to just past the tallest bar.
function yTicks(max) {
	const step = [0.01, 0.02, 0.025, 0.05, 0.1, 0.2, 0.25].find((s) => max / s <= 4) || 0.25;
	const top = Math.ceil((max * 1.08) / step) * step;
	return { top, ticks: Array.from({ length: Math.round(top / step) + 1 }, (_, i) => +(i * step).toFixed(4)) };
}

function NullChart({ n, count, pi0, expected, probs = [], counted = [], lower, upper, p, countNoun = "yes", rateNoun = "share of yeses" }) {
	const [hover, setHover] = useState(null);
	if (!probs.length || count == null) return null;
	const plural = countNoun === "yes" ? "yeses" : `${countNoun}s`;
	const many = (k) => `${k} ${k === 1 ? countNoun : plural}`;
	const observed = probs[count];
	const mode = probs.indexOf(Math.max(...probs));
	const { top, ticks } = yTicks(probs[mode]);
	const B = H - M.bottom, l = M.left, r = W - M.right;
	const slot = (r - l) / probs.length;
	const barW = Math.max(1, slot * 0.78);
	const cx = (k) => l + slot * (k + 0.5);
	const y = (v) => B - (v / top) * (B - M.top - 8);
	const ifNull = `if the ${rateNoun} were ${fmtR(pi0, 2)}`;
	const runText = (run) => (run.from === run.to ? many(run.from) : run.to === n ? `${run.from} or more` : run.from === 0 ? `${run.to} or fewer` : `${run.from} to ${run.to}`);
	const runs = [lower, upper].filter(Boolean);
	const sumText = runs.map((run) => fmtR(run.sum)).join(" + ");
	// Bracket each counted run just above its tallest bar (and above the cut line), with its sum.
	const bracket = (run, side) => {
		const tallest = Math.max(...probs.slice(run.from, run.to + 1), observed);
		const by = y(tallest) - 8, x1 = cx(run.from) - slot / 2, x2 = cx(run.to) + slot / 2;
		// The lower run's label sits at its left edge, clear of the rising bars; the upper run's at its middle.
		const tx = side === "lower" ? x1 : Math.min((x1 + x2) / 2, r - 70);
		const anchor = side === "lower" ? "start" : "middle";
		return (
			<g key={side} pointerEvents="none">
				<path d={`M${x1},${by + 5} V${by} H${x2} V${by + 5}`} fill="none" stroke={INK} />
				<Halo x={tx} y={by - 18} textAnchor={anchor} fill={INK} fontWeight="600">{upperFirst(runText(run))} {plural}</Halo>
				<Halo x={tx} y={by - 5} textAnchor={anchor} fill={INK}>{fmtR(run.sum)} in all</Halo>
			</g>
		);
	};
	const middle = lower && upper && upper.from - lower.to > 2 ? [lower.to + 1, upper.from - 1] : null;
	const xTicks = Array.from({ length: Math.floor(n / 5) + 1 }, (_, i) => i * 5);
	return (
		<figure>
			<Legend items={[
				{ label: `Chance of each count ${ifNull}`, color: CHART_1, opacity: 0.3 },
				{ key: "counted", label: <>Counted in <em>p</em>: no more likely than {count}</>, color: CHART_1 },
				{ label: `This study: ${many(count)} of ${n}`, color: CHART_2 },
				{ label: `Chance of ${count}: ${fmtR(observed)}`, color: INK, kind: "dash" }
			]} />
			<svg viewBox={`0 0 ${W} ${H}`} className="mt-2 w-full" role="img" onMouseLeave={() => setHover(null)}
				aria-label={`Bars for every possible count from 0 to ${n} ${plural}, each the chance of that count ${ifNull}. The most likely is ${mode}, at ${fmtR(probs[mode])}. This study's ${count} has chance ${fmtR(observed)}. The counts no more likely than that, ${runs.map((run) => `${runText(run)} (${fmtR(run.sum)})`).join(" and ")}, add up to p ${pRel(p)}.`}>
				{ticks.map((t) => (
					<g key={t}>
						<line x1={l} x2={r} y1={y(t)} y2={y(t)} stroke={GRID} />
						<text x={l - 6} y={y(t)} fontSize="11" fill={MUTED} textAnchor="end" dominantBaseline="middle">{t === 0 ? "0" : fmtR(t, 2)}</text>
					</g>
				))}
				<text x={l} y={M.top - 4} fontSize="11" fill={MUTED}>Probability</text>
				{probs.map((v, k) => (
					<rect key={k} x={cx(k) - barW / 2} y={y(v)} width={barW} height={Math.max(0, B - y(v))}
						fill={k === count ? CHART_2 : CHART_1} opacity={(counted[k] ? 1 : 0.3) * (hover === null || hover === k ? 1 : 0.6)} />
				))}
				{/* The cut: every bar no taller than the observed count's goes into p. */}
				<line x1={l} x2={r} y1={y(observed)} y2={y(observed)} stroke={INK} strokeDasharray="4 3" pointerEvents="none" />
				{/* Where the null hypothesis expects the count to fall. */}
				<line x1={l + slot * (expected + 0.5)} x2={l + slot * (expected + 0.5)} y1={y(probs[mode]) - 4} y2={B} stroke={MUTED} strokeDasharray="2 3" pointerEvents="none" />
				<Halo x={l + slot * (expected + 0.5) + 6} y={y(probs[mode]) - 2} fill={MUTED}>Expected: {fmtCount(expected)}</Halo>
				{lower && bracket(lower, "lower")}
				{upper && bracket(upper, "upper")}
				<Halo x={r} y={M.top + 4} textAnchor="end" fill={INK} fontWeight="600">
					Two-sided <tspan fontStyle="italic">p</tspan> {runs.length > 1 ? `= ${sumText} ` : ""}{pRel(p)}
				</Halo>
				<line x1={l} x2={r} y1={B} y2={B} stroke={GRID} />
				{xTicks.map((t) => (
					<text key={t} x={cx(t)} y={B + 16} fontSize="11" fill={t === count ? INK : MUTED} fontWeight={t === count ? 600 : 400} textAnchor="middle">{t}</text>
				))}
				{!xTicks.includes(count) && <text x={cx(count)} y={B + 16} fontSize="11" fill={INK} fontWeight="600" textAnchor="middle">{count}</text>}
				<text x={r} y={H - 4} fontSize="11" fill={MUTED} textAnchor="end">{upperFirst(plural)} out of {n}</text>
				{/* Hit targets: each count's full column. */}
				{probs.map((v, k) => (
					<rect key={`hit${k}`} x={cx(k) - slot / 2} y={M.top} width={slot} height={B - M.top} fill="transparent" onMouseEnter={() => setHover(k)} />
				))}
				{hover !== null && (
					<Tooltip x={cx(hover)} lines={[
						`${upperFirst(many(hover))} out of ${n}${hover === count ? " (this study)" : ""}`,
						`Chance ${fmtProb(probs[hover])} ${ifNull}`,
						counted[hover] ? `Counted in p: no more likely than ${count}` : `Not counted: more likely than ${count}`
					]} />
				)}
			</svg>
			<figcaption className="mt-1 text-xs text-gray-600">
				Each bar is the chance of exactly that many {plural} out of {n} {ifNull}; the most likely count is {mode}. The dashed line marks the chance of this study's {count} ({fmtR(observed)}).
				{" "}<em>p</em> adds up every bar no taller than that, on both sides: {runs.map((run) => `${runText(run)} ${plural} (${fmtR(run.sum)})`).join(" and ")}, so <em>p</em> {pRel(p)}.
				{middle && ` Every count from ${middle[0]} to ${middle[1]} is more likely than ${count}, so none of them counts.`}
			</figcaption>
		</figure>
	);
}

// ---- binomial_power: the same null distribution beside the one under a true proportion π₁ ----

// Each count k gets two bars side by side: P(X = k) if the rate were π₀ (teal) and if it were π₁
// (brown). The counts binom.test calls significant at α, two-sided, sit on a shaded band (the
// rejection region); there the bars are solid, elsewhere pale. A bracket over each run of the
// region gives its chance under each rate, so the brown bars on the band add up to power and the
// teal ones to the test's false-positive rate. Props come from ChartData#binomial_power:
//   n, pi0, pi1, alpha, expected, probs, altProbs, significant [bool],
//   lower / upper ({ from, to, size, power } or null), size, power.
const fmtPct = (x) => (x > 0 && x < 0.001 ? "< 0.1%" : `${(x * 100).toFixed(1)}%`);

function PowerChart({ n, pi0, pi1, alpha = 0.05, probs = [], altProbs = [], significant = [], lower, upper, size, power, countNoun = "yes", rateNoun = "share of yeses" }) {
	const [hover, setHover] = useState(null);
	if (!probs.length || !altProbs.length) return null;
	const plural = countNoun === "yes" ? "yeses" : `${countNoun}s`;
	const many = (k) => `${k} ${k === 1 ? countNoun : plural}`;
	const { top, ticks } = yTicks(Math.max(...probs, ...altProbs));
	const B = H - M.bottom, l = M.left, r = W - M.right;
	const slot = (r - l) / probs.length;
	const barW = Math.max(1, slot * 0.4);
	const cx = (k) => l + slot * (k + 0.5);
	const y = (v) => B - (v / top) * (B - M.top - 8);
	const rate0 = fmtR(pi0, 2), rate1 = fmtR(pi1, 2);
	const ifRate = (pi) => `if the ${rateNoun} were ${fmtR(pi, 2)}`;
	const runs = [lower, upper].filter(Boolean);
	const runText = (run) => (run.from === run.to ? String(run.from) : run.to === n ? `${run.from} or more` : run.from === 0 ? `${run.to} or fewer` : `${run.from} to ${run.to}`);
	const gap = lower && upper && upper.from - lower.to > 1 ? [lower.to + 1, upper.from - 1] : null;
	// Bracket each run just above its tallest bar (either rate), with its chance under each.
	const bracket = (run, side) => {
		const tallest = Math.max(...probs.slice(run.from, run.to + 1), ...altProbs.slice(run.from, run.to + 1));
		const by = y(tallest) - 8, x1 = cx(run.from) - slot / 2, x2 = cx(run.to) + slot / 2;
		// The lower run's label sits at its left edge, clear of the rising bars; the upper run's at its middle.
		const tx = side === "lower" ? x1 : Math.min((x1 + x2) / 2, r - 70);
		const anchor = side === "lower" ? "start" : "middle";
		return (
			<g key={side} pointerEvents="none">
				<path d={`M${x1},${by + 5} V${by} H${x2} V${by + 5}`} fill="none" stroke={INK} />
				<Halo x={tx} y={by - 31} textAnchor={anchor} fill={INK} fontWeight="600">{upperFirst(runText(run))} {plural}</Halo>
				<Halo x={tx} y={by - 18} textAnchor={anchor} fill={CHART_1}>If {rate0}: {fmtPct(run.size)}</Halo>
				<Halo x={tx} y={by - 5} textAnchor={anchor} fill={CHART_3} fontWeight="600">If {rate1}: {fmtPct(run.power)}</Halo>
			</g>
		);
	};
	const xTicks = Array.from({ length: Math.floor(n / 5) + 1 }, (_, i) => i * 5);
	return (
		<figure>
			<Legend items={[
				{ label: `Chance of each count ${ifRate(pi0)}`, color: CHART_1 },
				{ label: `Chance ${ifRate(pi1)}`, color: CHART_3 },
				{ key: "region", label: <>Counts the test calls significant (α = {fmtR(alpha, 2)}, two-sided)</>, color: BAND_EDGE }
			]} />
			<svg viewBox={`0 0 ${W} ${H}`} className="mt-2 w-full" role="img" onMouseLeave={() => setHover(null)}
				aria-label={`Two bars for every possible count from 0 to ${n} ${plural}: its chance ${ifRate(pi0)}, and ${ifRate(pi1)}. The test calls ${runs.map(runText).join(" and ")} ${plural} significant. ${upperFirst(ifRate(pi0))}, those counts turn up ${fmtPct(size)} of the time; ${ifRate(pi1)}, ${fmtPct(power)} of the time, which is the test's power.`}>
				{/* The rejection region: a band behind each run of significant counts. */}
				{runs.map((run) => (
					<rect key={`band${run.from}`} x={cx(run.from) - slot / 2} y={M.top} width={slot * (run.to - run.from + 1)} height={B - M.top} fill={BAND} pointerEvents="none" />
				))}
				{ticks.map((t) => (
					<g key={t}>
						<line x1={l} x2={r} y1={y(t)} y2={y(t)} stroke={GRID} />
						<text x={l - 6} y={y(t)} fontSize="11" fill={MUTED} textAnchor="end" dominantBaseline="middle">{t === 0 ? "0" : fmtR(t, 2)}</text>
					</g>
				))}
				<text x={l} y={M.top - 4} fontSize="11" fill={MUTED}>Probability</text>
				{probs.map((v, k) => {
					const fade = (significant[k] ? 1 : 0.3) * (hover === null || hover === k ? 1 : 0.6);
					return (
						<g key={k}>
							<rect x={cx(k) - barW} y={y(v)} width={barW} height={Math.max(0, B - y(v))} fill={CHART_1} opacity={fade} />
							<rect x={cx(k)} y={y(altProbs[k])} width={barW} height={Math.max(0, B - y(altProbs[k]))} fill={CHART_3} opacity={fade} />
						</g>
					);
				})}
				{lower && bracket(lower, "lower")}
				{upper && bracket(upper, "upper")}
				<Halo x={r} y={M.top + 4} textAnchor="end" fill={CHART_3} fontWeight="600">Power = {fmtPct(power)}</Halo>
				<Halo x={r} y={M.top + 18} textAnchor="end" fill={CHART_1}>False positives = {fmtPct(size)}</Halo>
				<line x1={l} x2={r} y1={B} y2={B} stroke={GRID} />
				{xTicks.map((t) => (
					<text key={t} x={cx(t)} y={B + 16} fontSize="11" fill={MUTED} textAnchor="middle">{t}</text>
				))}
				<text x={r} y={H - 4} fontSize="11" fill={MUTED} textAnchor="end">{upperFirst(plural)} out of {n}</text>
				{/* Hit targets: each count's full column. */}
				{probs.map((v, k) => (
					<rect key={`hit${k}`} x={cx(k) - slot / 2} y={M.top} width={slot} height={B - M.top} fill="transparent" onMouseEnter={() => setHover(k)} />
				))}
				{hover !== null && (
					<Tooltip x={cx(hover)} lines={[
						`${upperFirst(many(hover))} out of ${n}`,
						`Chance ${fmtProb(probs[hover])} ${ifRate(pi0)}`,
						`Chance ${fmtProb(altProbs[hover])} ${ifRate(pi1)}`,
						significant[hover] ? `Significant: p ≤ ${fmtR(alpha, 2)}` : "Not significant"
					]} />
				)}
			</svg>
			<figcaption className="mt-1 text-xs text-gray-600">
				Teal bars are the chance of each count out of {n} {ifRate(pi0)}; brown bars, {ifRate(pi1)}.
				{" "}The test calls a count significant if it's {runs.map(runText).join(" or ")} {plural} (shaded).
				{" "}{upperFirst(ifRate(pi0))}, that happens {fmtPct(size)} of the time: the test's false-positive rate. {upperFirst(ifRate(pi1))}, it happens {fmtPct(power)} of the time: that's the power.
				{gap && ` The other ${fmtPct(1 - power)} of the brown bars fall between ${gap[0]} and ${gap[1]}, where a real ${rateNoun} of ${rate1} would be missed.`}
			</figcaption>
		</figure>
	);
}

export default function BinomialChart(props) {
	return props.altProbs ? <PowerChart {...props} /> : <NullChart {...props} />;
}
